import { Sparkles, User } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { ChatMessage as ChatMessageType } from './aiAssistantStore';

/**
 * Strip the trailing `<chips>[...]</chips>` block from the message body so
 * it never appears in the rendered prose (it's only meant for the chip
 * picker). Kept here so streaming partials are cleaned in-flight too.
 */
function stripChips(content: string): string {
  return content.replace(/<chips>[\s\S]*?<\/chips>\s*$/i, '').trimEnd();
}

export function ChatMessage({ message }: { message: ChatMessageType }) {
  const isUser = message.role === 'user';
  const text = isUser ? message.content : stripChips(message.content);

  return (
    <div
      className={cn(
        'flex gap-2 px-3 py-2',
        isUser ? 'flex-row-reverse' : 'flex-row',
      )}
    >
      <div
        className={cn(
          'grid h-7 w-7 flex-none place-items-center rounded-full',
          isUser
            ? 'bg-[var(--color-xk-surface)] text-[var(--color-xk-lavender)]'
            : 'bg-[var(--color-accent-10)] text-[var(--color-xk-accent)]',
        )}
      >
        {isUser ? (
          <User className="h-3.5 w-3.5" />
        ) : (
          <Sparkles className="h-3.5 w-3.5" />
        )}
      </div>
      <div
        className={cn(
          'min-w-0 flex-1 whitespace-pre-wrap break-words rounded-md px-3 py-2 text-[13px] leading-relaxed',
          isUser
            ? 'bg-[var(--color-xk-surface)] text-[var(--color-xk-lavender)]'
            : 'bg-[var(--color-xk-bg)] text-[var(--color-xk-lavender)]',
        )}
      >
        {text}
        {message.pending && !text && (
          <span className="inline-flex gap-1 align-middle text-[var(--color-xk-text-muted)]">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current" />
            <span
              className="h-1.5 w-1.5 animate-pulse rounded-full bg-current"
              style={{ animationDelay: '150ms' }}
            />
            <span
              className="h-1.5 w-1.5 animate-pulse rounded-full bg-current"
              style={{ animationDelay: '300ms' }}
            />
          </span>
        )}
      </div>
    </div>
  );
}
